import { richTextToPlainText } from '@govinity/shared-config';
import { resolveVisibility } from './resolve-visibility.js';
import { FieldType, TIME_REGEX } from './index.js';
/**
 * Checks whether a custom field value is empty (null, undefined, or type-appropriate blank).
 * Handles all field types: strings, arrays, checkbox groups, and primitives.
 */
export function isCustomFieldValueEmpty(value, type) {
    if (value === null || value === undefined) {
        return true;
    }
    if (type === FieldType.MULTI_SELECT) {
        return !Array.isArray(value) || value.length === 0;
    }
    if (type === FieldType.CHECKBOX_GROUP) {
        if (typeof value !== 'object' || Array.isArray(value))
            return true;
        return !Object.values(value).some(Boolean);
    }
    if (type === FieldType.RICH_TEXT) {
        return typeof value !== 'string' || richTextToPlainText(value) === '';
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    return false;
}
/**
 * Validates a custom field value based on its type
 */
function validateCustomFieldValue(value, type, label, required) {
    // Handle null/undefined values for required fields
    if (required && (value === null || value === undefined)) {
        return `${label} is required`;
    }
    // For non-required fields, null/undefined is valid
    if (!required && (value === null || value === undefined)) {
        return null;
    }
    // If we have a value, validate its type regardless of whether it's required
    switch (type) {
        case FieldType.TEXT:
        case FieldType.TEXTAREA:
        case FieldType.SUGGESTED_TEXT:
            if (typeof value !== 'string') {
                return `${label} must be text`;
            }
            if (required && value.trim() === '') {
                return `${label} is required`;
            }
            break;
        case FieldType.RICH_TEXT:
            if (typeof value !== 'string') {
                return `${label} must be text`;
            }
            if (required && richTextToPlainText(value) === '') {
                return `${label} is required`;
            }
            break;
        case FieldType.SELECT:
            if (typeof value !== 'string') {
                return `${label} must be a valid selection`;
            }
            if (required && value.trim() === '') {
                return `${label} is required`;
            }
            break;
        case FieldType.MULTI_SELECT:
            if (!Array.isArray(value)) {
                return `${label} must be an array of selections`;
            }
            if (required && value.length === 0) {
                return `${label} is required`;
            }
            // Validate that all values are strings
            for (const item of value) {
                if (typeof item !== 'string') {
                    return `${label} must contain valid string selections`;
                }
            }
            break;
        case FieldType.CHECKBOX:
            if (typeof value !== 'boolean') {
                return `${label} must be a valid checkbox value`;
            }
            break;
        case FieldType.CHECKBOX_GROUP:
            if (typeof value !== 'object' || value === null || Array.isArray(value)) {
                return `${label} must be an object of checkbox selections`;
            }
            if (required) {
                const checkboxValues = value;
                const hasSelectedValue = Object.values(checkboxValues).some(Boolean);
                if (!hasSelectedValue) {
                    return `${label} is required`;
                }
            }
            // Validate that all values are booleans
            for (const [, item] of Object.entries(value)) {
                if (typeof item !== 'boolean') {
                    return `${label} must contain valid boolean selections`;
                }
            }
            break;
        case FieldType.DATE:
            if (typeof value !== 'string') {
                return `${label} must be a valid date`;
            }
            if (required && value.trim() === '') {
                return `${label} is required`;
            }
            // Validate date format and ensure it's a real date
            const dateValue = value.trim();
            if (dateValue !== '') {
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) {
                    return `${label} must be a valid date`;
                }
            }
            break;
        case FieldType.TIME:
            if (typeof value !== 'string') {
                return `${label} must be a valid time`;
            }
            if (required && value.trim() === '') {
                return `${label} is required`;
            }
            // Validate time format (HH:MM)
            const timeValue = value.trim();
            if (timeValue !== '') {
                if (!TIME_REGEX.test(timeValue)) {
                    return `${label} must be a valid time in HH:MM format`;
                }
            }
            break;
        case FieldType.NUMBER:
            if (typeof value !== 'number') {
                return `${label} must be a valid number`;
            }
            if (required && (value === null || value === undefined)) {
                return `${label} is required`;
            }
            // Check if it's a valid number (not NaN)
            if (isNaN(value)) {
                return `${label} must be a valid number`;
            }
            break;
        case FieldType.CURRENCY:
            if (typeof value !== 'number') {
                return `${label} must be a valid currency amount`;
            }
            if (required && (value === null || value === undefined)) {
                return `${label} is required`;
            }
            // Check if it's a valid number (not NaN)
            if (isNaN(value)) {
                return `${label} must be a valid currency amount`;
            }
            break;
        default:
            // Throw error for unknown field types to catch unimplemented cases early
            throw new Error(`Unknown field type: ${type} for field: ${label}`);
    }
    return null; // No validation error
}
/**
 * Builds a visibility map keyed by instance ID using the shared resolver.
 */
function buildVisibilityMap(customFields, customFieldValues) {
    const record = resolveVisibility(customFields.map((f) => ({
        id: f.id,
        customFieldId: f.customFieldId,
        parentId: f.parentId ?? null,
        parentConditionValue: f.parentConditionValue ?? null,
    })), customFieldValues);
    return new Map(Object.entries(record).map(([k, v]) => [Number(k), v]));
}
/**
 * Returns a Zod schema for a form that validates all custom fields.
 * @param baseSchema The base schema to extend with custom field validation
 * @param customFields Array of all custom field IDs, labels, types, and required status
 * @param opts Optional options object
 * @param opts.enforceRequired Whether to enforce required fields
 * @param opts.additionalRefinement Extra validation to run in the same superRefine pass
 *   as custom field validation, guaranteeing all errors surface simultaneously.
 */
export function getFormSchemaWithCustomFieldRequirements(baseSchema, customFields, opts) {
    const { enforceRequired = true, additionalRefinement } = opts || {};
    return baseSchema.superRefine((data, ctx) => {
        additionalRefinement?.(data, ctx);
        const visibilityMap = buildVisibilityMap(customFields, data.customFieldValues ?? []);
        // OR-reduce visibility by customFieldId: a field is visible if ANY instance is visible.
        // Also track whether any visible instance is required.
        const visibleByCustomFieldId = new Map();
        const requiredByCustomFieldId = new Map();
        for (const field of customFields) {
            const isVisible = visibilityMap.get(field.id) ?? false;
            visibleByCustomFieldId.set(field.customFieldId, (visibleByCustomFieldId.get(field.customFieldId) ?? false) || isVisible);
            if (isVisible && field.required) {
                requiredByCustomFieldId.set(field.customFieldId, true);
            }
        }
        const validated = new Set();
        customFields.forEach((field) => {
            if (validated.has(field.customFieldId))
                return;
            validated.add(field.customFieldId);
            if (!visibleByCustomFieldId.get(field.customFieldId))
                return;
            const isRequired = requiredByCustomFieldId.get(field.customFieldId) ?? false;
            const findValue = (v) => v.customFieldId === field.customFieldId;
            const found = data.customFieldValues?.find(findValue);
            if (!found) {
                if (isRequired && enforceRequired) {
                    ctx.addIssue({
                        code: 'custom',
                        message: `${field.label} is required`,
                        path: ['customFieldValues'],
                    });
                }
                return;
            }
            const validationError = validateCustomFieldValue(found.value, field.type, field.label, isRequired && enforceRequired);
            if (validationError) {
                const fieldIndex = data.customFieldValues?.findIndex(findValue);
                if (fieldIndex !== undefined && fieldIndex >= 0) {
                    ctx.addIssue({
                        code: 'custom',
                        message: validationError,
                        path: ['customFieldValues', fieldIndex, 'value'],
                    });
                }
            }
        });
    });
}
